import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IconBuilding, IconCheck, IconChevronDown } from "@tabler/icons-react";
import { toast } from "sonner";
import { useAuth } from "../../hooks/useAuth";
import { useAuthOrganizationsQuery } from "../../api/queries/authOrganizationsQueries";
import {
  extractMfaClaim,
  extractScopes,
  getTokenClaims,
} from "../../utils/authStorage";
import { resolvePostLoginPath } from "../../utils/navigationScopes";

export function OrgSwitcher() {
  const { orgId, switchOrg, isAuthenticated, mfaClaim } = useAuth();
  const enabled = isAuthenticated && mfaClaim !== "pending";
  const { data: organizations = [] } = useAuthOrganizationsQuery(enabled);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  if (organizations.length <= 1) {
    return null;
  }

  const active =
    organizations.find((org) => org.org_id === orgId) ?? organizations[0];

  const handleSelect = async (targetOrgId: number) => {
    if (targetOrgId === orgId || switching) return;
    setSwitching(true);
    try {
      const result = await switchOrg(targetOrgId);
      setOpen(false);

      const claims = getTokenClaims(result.access_token) ?? {};
      const nextMfa = extractMfaClaim(claims);
      if (nextMfa === "pending") {
        navigate("/mfa", { replace: true, state: { from: location } });
        toast.message("Verify MFA for this organization");
        return;
      }

      const nextScopes = extractScopes(claims);
      const nextPath = resolvePostLoginPath(location.pathname, nextScopes);
      if (nextPath !== location.pathname) {
        navigate(nextPath, { replace: true });
      }
      toast.success("Switched organization");
    } catch {
      toast.error("Could not switch organization");
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={switching}
        className="flex max-w-[220px] items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-60"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <IconBuilding size={16} stroke={1.5} className="shrink-0 text-gray-500" />
        <span className="truncate font-medium">{active?.org_name ?? "Organization"}</span>
        <IconChevronDown size={14} stroke={1.5} className="ml-auto shrink-0 text-gray-400" />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 z-50 mt-2 max-h-64 w-64 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        >
          {organizations.map((org) => {
            const selected = org.org_id === orgId;
            return (
              <li key={org.org_id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={switching}
                  onClick={() => void handleSelect(org.org_id)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  <span className="min-w-0 flex-1 truncate">{org.org_name}</span>
                  {selected ? (
                    <IconCheck size={16} stroke={1.5} className="shrink-0 text-emerald-600" />
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
